// src/utils/utils_paymentPopup.ts
//
// PayPal/Stripe ne savent que rediriger le NAVIGATEUR (return_url/cancel_url côté API,
// cf. BankProcessor_Paypal/Stripe) — pas de callback ni d'iframe. Rediriger l'onglet
// principal recharge toute la SPA au retour : la session Redux n'étant pas persistée,
// ça ramenait au login au lieu de "là où j'étais avant" (signalé par Alex, 2026-09-14).
//
// Solution : ouvrir la banque dans une FENÊTRE À PART. L'onglet admin-front ne quitte
// jamais la page — payment_success.tsx/payment_failure.tsx, chargées dans cette fenêtre
// à son retour, se contentent de prévenir l'onglet d'origine (postMessage) puis de se
// refermer. Les deux pages restent fonctionnelles seules (lien copié, favori, popup
// bloquée) : window.opener y est simplement absent dans ce cas.

const POPUP_NAME = 'kleon_payment_popup';
const MESSAGE_SOURCE = 'kleon-payment';

export type PaymentResultStatus = 'success' | 'failure';

export interface PaymentResultMessage {
  source: typeof MESSAGE_SOURCE;
  status: PaymentResultStatus;
}

/**
 * À appeler de façon SYNCHRONE dans le gestionnaire de clic (avant tout await) : un
 * navigateur bloque window.open() hors d'un geste utilisateur direct, mais pas la
 * navigation ultérieure d'une fenêtre déjà ouverte (cf. redirectPaymentPopup).
 *
 * @returns null si le navigateur a bloqué l'ouverture (popups désactivés).
 */
export function openPaymentPopup(): Window | null {
  return window.open('', POPUP_NAME, 'width=480,height=720');
}

/** Envoie la fenêtre déjà ouverte vers l'url de paiement une fois celle-ci connue. */
export function redirectPaymentPopup(popup: Window, url: string): void {
  popup.location.href = url;
  popup.focus();
}

/**
 * Écoute le résultat renvoyé par payment_success.tsx/payment_failure.tsx depuis la
 * fenêtre de paiement. Filtré par origine ET par un marqueur applicatif — un postMessage
 * peut venir de n'importe quel script de la page (extensions, autres widgets).
 *
 * @returns fonction de nettoyage (à appeler dans le useEffect qui a posé l'écoute).
 */
export function listenForPaymentResult(onResult: (status: PaymentResultStatus) => void): () => void {
  const handler = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;

    const data = event.data as Partial<PaymentResultMessage> | undefined;

    if (data?.source === MESSAGE_SOURCE && (data.status === 'success' || data.status === 'failure')) {
      onResult(data.status);
    }
  };

  window.addEventListener('message', handler);

  return () => window.removeEventListener('message', handler);
}

/** À appeler depuis payment_success.tsx/payment_failure.tsx. Sans effet si pas ouvert en popup. */
export function notifyPaymentResultToOpener(status: PaymentResultStatus): void {
  if (!window.opener) return;

  const message: PaymentResultMessage = { source: MESSAGE_SOURCE, status };

  window.opener.postMessage(message, window.location.origin);
}
